/* tslint:disable:typedef-whitespace no-trailing-whitespace no-inferrable-types import-spacing */

import { ElementRef }         from '@angular/core';

import { Dialog }             from './dialog';
import { Logger }             from '@/util/logger';

import {FAVRecord} from "@/models/project/f.a.v.record";
import {LocomotiveDialogParams} from "@/gui/dialogs/locomotive.dialog.params";

import { IValidatorCallBack } from './i.validator.call.back';
import { IOkCallBack        } from './i.ok.call.back';

import * as $                 from 'jquery';
import 'jquery-ui-dist/jquery-ui';

import 'select2';
import {OptGroupData, OptionData, SearchOptions} from 'select2';
import KeyDownEvent = JQuery.KeyDownEvent;
import KeyUpEvent = JQuery.KeyUpEvent;
import {ServiceLocator} from "@/services/service.locator";
import {ServiceManager} from "@/services/service.manager";

/**
 *
 * @class NewLocomotiveModelDialog
 *
 * Dialog for gathering parameters needed to create a locomotive model via BigQuery and
 * the locoquery service.
 *
 * We use jQueryUI date picker for date selection: https://jqueryui.com/datepicker/
 *
 * We use Select2: https://select2.org  for the FAV "combo box".
 *
 * @note The select2 @types/select2 (definitely typed stuff), includes "requirejs" which is
 * not going to be compatible with Angular.  Just edit its index.d.ts file and remove the
 * requirejs stuff after you add it to your npm repo.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      January 2022
 *
 */
export class NewLocomotiveModelDialog extends Dialog {

  /** the name we're trying get from the user */
  private name: string   = '';

  /** the FAV we are parameterizing the locomotive model with */
  private fav: FAVRecord = null;

  /** the start of the time bound */
  private start: string  = '';

  /** the end of the time bound */
  private end: string    = '';

  /** timer to wait for user to stop typing */
  private typingTimer = null;

  /** standard constructor */
  constructor(host: ElementRef) {

    super(host);

    this.title = 'New Locomotive Model';
    this.icon  = '<i class="fas fa-plus"></i>';

    Logger.debug('[NewLocomotiveModelDialog] setting up...');
  }

  /** dialog has been opened */
  public open(validator?: IValidatorCallBack, success?: IOkCallBack, options?: any) {

    let nlmd: NewLocomotiveModelDialog = this;

    this.validator = validator ? validator : null;
    this.success   = success   ? success   : null;

    if (this.typingTimer) {

      clearTimeout(this.typingTimer);

      this.typingTimer = null;
    }

    Logger.debug('[NewLocomotiveModelDialog] opening...');

    this.name = '';

    const body = $(this.host).find('.modal-body');

    const html = `
<div id="locomotive-name-field" style="margin-top: 12px;">
<label class="gui-label" style="text-align: right; width: 60px;">Name:</label>
<input class="gui-input locomotive-name-input" style="width: 200px;" type="text" placeholder="Loco Model Name">
</div>

<div id="locomotive-fav-field" style="margin-top: 12px;">
<label class="gui-label" style="text-align: right; width: 60px;">FAV:</label>
<select class="gui-input locomotive-fav-input fav-select"><option></option></select>
</div>

<div id="locomotive-start-field" style="margin-top: 12px;">
<label class="gui-label" style="text-align: right; width: 60px;">Start:</label>
<input class="gui-input locomotive-start-input" style="width: 200px;" type="text" placeholder="...">
</div>

<div id="locomotive-end-field" style="margin-top: 12px;">
<label class="gui-label" style="text-align: right; width: 60px;">End:</label>
<input class="gui-input locomotive-end-input" style="width: 200px;" type="text" placeholder="...">
</div>

<div class="gui-error-line"></div>
    `;

    $(body).html(html);

    /* set the start picker */

    $('.locomotive-start-input').datepicker({

      dateFormat: 'yy-mm-dd',

      onSelect: (date) => {

        /* jQuery datepicker uses format */

        Logger.debug(`[NewLocomotiveModelDialog] new start: `, date);

        nlmd.start = date;

        this.validate();
      }

    });

    /* set the end picker */

    $('.locomotive-end-input').datepicker({

      dateFormat: 'yy-mm-dd',

      onSelect: (date) => {

        Logger.debug(`[NewLocomotiveModelDialog] new end: `, date);

        nlmd.end = date;

        this.validate();
      }

    });

    /* set up the FAV selection drop down */

    $('.fav-select').select2({

      placeholder:   `Please Wait...`,
      disabled:      true,
      allowClear:    false,
      closeOnSelect: true,
      width:         '200px',
      data:          []
    });

    /* add behaviors */

    setTimeout(() => {

      $(this.host).find('.locomotive-name-input').focus();

      $(this.host).find('.locomotive-name-input').on('input', (e) => {

        /* typing wait: @link https://stackoverflow.com/questions/10124114/wait-for-function-till-user-stops-typing */

        if (this.typingTimer) {

          clearTimeout(this.typingTimer);
        }

        this.typingTimer = setTimeout(() => {

          this.name = ($(e.currentTarget).val() as string).trim();

          this.validate();

        }, 400);

      });

      $(this.host).find('.locomotive-name-input').on('keypress', (e) => {

        if (this.typingTimer) {

          clearTimeout(this.typingTimer);
        }

        this.typingTimer = setTimeout(() => {

          this.name = ($(e.currentTarget).val() as string).trim();

          if (e.key.toLowerCase() === 'enter') {

            this.validate(true);

            /* stop event propagation */

            return false;
          }

        }, 400);

      });

    }, 50);

    /* disable the Ok button...until we have valid input */

    this.validate();

    /* launch the data fill for the select2 dropdown (the FAV picker) */

    let sm: ServiceManager            = ServiceLocator.injector.get(ServiceManager);
    sm.databridge.favList().then(
      (result: FAVRecord[]) => {

        Logger.debug(`[NewLocomotiveModelDialog] configuring drop down: `, result);

        /*
         * format the data per select2 needs ...
         *
         * https://select2.org/data-sources/formats
         *
         */

        let data: any[] = [];

        result.forEach((item: FAVRecord) => {

          data.push({
            "id": item.ID,
            "text": item.name,
            "fav": item
          });

        });

        /* sort the data by name ... */

        data.sort((a, b) => {

          return a.text.localeCompare(b.text);
        });

        /* set the combo box to this set of data */

        $('.fav-select').select2('destroy');

        $('.fav-select').select2({

          placeholder: `Select...`,
          disabled: false,
          allowClear: false,
          closeOnSelect: true,
          width: '200px',
          data
        });

        /* should be good to go for selecting... */

        $('.fav-select').on('select2:select', (e) => {

          nlmd.fav = e.params.data['fav'];

          Logger.debug('[NewLocomotiveModelDialog] FAV picked: ', nlmd.fav);

          this.validate();
        });

      },
      (error) => {

        Logger.error("[NewLocomotiveModelDialog] could not fetch FAV list: ", error);



      });
  }

  disable() {

    const ok = $(this.host).find('.modal-ok-button');

    $(ok).attr('disabled', 'true');
    $(ok).addClass('disabled');
  }

  enable() {

    const ok = $(this.host).find('.modal-ok-button');

    $(ok).removeAttr('disabled');
    $(ok).removeClass('disabled');

  }

  /** show a validation error */
  error(msg: string) {

    const display = $(this.host).find('.gui-error-line');

    $(display).html(msg);
  }

  /* don't enable the Ok button until we have what we need. */
  validate(transition: boolean = false) {

    let nlmd: NewLocomotiveModelDialog = this;

    nlmd.error('');

    /* assume its bad */

    nlmd.disable();

    if (!this.validator) {

      /* at least make sure the name isn't empty */

      if (nlmd.name === '') {

        const display = $(this.host).find('.gui-error-line');

        $(display).html('Model name can not be empty.');

        return ;
      }

      if (nlmd.fav === null) {

        const display = $(nlmd.host).find('.gui-error-line');

        $(display).html('Must select a FAV');

        return ;
      }

      if (nlmd.start === '') {

        const display = $(this.host).find('.gui-error-line');

        $(display).html('Must select a start date');

        return ;
      }

      if (nlmd.end === '') {

        const display = $(this.host).find('.gui-error-line');

        $(display).html('Must select an end date');

        return ;
      }

      nlmd.enable();

      /* if we make it through validation checks, and we're allowed to transition, then do it. */

      if (transition) {

        /* click the ok button for them, and trigger via ModelComponent */

        const ok = $(nlmd.host).find('.modal-ok-button');

        $(ok).click();
      }

    } else {

      /* wrap validation in a promise, so it's ok if its asynchronous */

      const p = new Promise((resolve, reject) => {

        nlmd.validator(new LocomotiveDialogParams({
           name:  nlmd.name,
           fav:   nlmd.fav,
           start: nlmd.start,
           end:   nlmd.end
          }),
          resolve,
          reject
        );

      }).then((value: LocomotiveDialogParams) => {

        nlmd.enable();

        nlmd.name = value.name;

        if (transition) {

          /* click the ok button for them, and trigger via ModelComponent */

          const ok = $(this.host).find('.modal-ok-button');

          $(ok).click();
        }

      }).catch((error) => {

        /* show the validation error */

        nlmd.error(error);

      });

    }

  }

  /** dialog is closing */
  public close() {

    Logger.debug('[NewLocomotiveModelDialog] closing...');
  }

  /** dialog was accepted */
  public ok() {

    Logger.debug('[NewLocomotiveModelDialog] accepted: ', this.name);

    if (this.success) {

      /* inform the client */

      this.success(
        new LocomotiveDialogParams({
          name:  this.name,
          fav:   this.fav,
          start: this.start,
          end:   this.end
        })
      );
    }

  }
}


